import { useState, useEffect, useRef } from "react";
import { 
  motion, 
  AnimatePresence, 
  useScroll, 
  useTransform,
  useSpring,
  useInView,
  animate
} from "framer-motion";
import "./index.css";
import {
  Plane,
  Shield,
  Clock,
  Star,
  MapPin,
  Phone,
  Mail,
  ChevronDown,
  Menu,
  X,
  Fuel,
  Users,
  Building,
  ArrowRight,
  Instagram,
  Linkedin,
  Facebook,
  Twitter,
  CheckCircle2,
} from "lucide-react";

// Navigation Component
function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Home", href: "#home" },
    { name: "Services", href: "#services" },
    { name: "About", href: "#about" },
    { name: "Gallery", href: "#gallery" },
    { name: "Contact", href: "#contact" },
  ];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? "bg-black/80 backdrop-blur-xl py-4 border-b border-white/5" : "bg-transparent py-6"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <a href="#home" className="flex items-center gap-3 group relative z-50">
          <div className="w-12 h-12 rounded-full border-2 border-gold flex items-center justify-center bg-black/50 group-hover:bg-gold/10 transition-colors">
            <span className="text-gold font-bold text-sm">EA</span>
          </div>
          <div className="hidden sm:block">
            <span className="text-gold font-semibold tracking-wider text-lg">EURO</span>
            <span className="text-white/80 font-light tracking-widest text-sm block -mt-1">AVIATION</span>
          </div>
        </a>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.name}
              href={link.href}
              className="text-white/70 hover:text-gold transition-colors text-sm tracking-wider uppercase font-medium relative group"
            >
              {link.name}
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gold transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
          <motion.a
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            href="#contact"
            className="ml-4 px-6 py-2.5 bg-gold text-black font-semibold text-sm tracking-wider uppercase hover:bg-gold-light transition-colors shadow-[0_0_20px_rgba(212,175,55,0.3)] hover:shadow-[0_0_30px_rgba(212,175,55,0.5)]"
          >
            Request Handling
          </motion.a>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden text-white p-2 relative z-50"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="md:hidden absolute top-0 left-0 right-0 bg-black/95 backdrop-blur-xl min-h-screen pt-24 px-6"
          >
            <div className="flex flex-col gap-8 items-center">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="text-white/70 hover:text-gold transition-colors text-2xl tracking-wider uppercase font-light"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.name}
                </a>
              ))}
              <a
                href="#contact"
                className="mt-4 px-8 py-4 bg-gold text-black font-semibold tracking-wider uppercase text-center w-full max-w-xs"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Request Handling
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}

// Counter Component
function Counter({ value, duration = 2 }: { value: number; duration?: number }) {
  const nodeRef = useRef<HTMLSpanElement>(null);
  const inView = useInView(nodeRef, { once: true, margin: "-100px" });
  
  useEffect(() => {
    const node = nodeRef.current;
    if (!node || !inView) return;

    const controls = animate(0, value, {
      duration,
      ease: "easeOut",
      onUpdate(value) {
        node.textContent = Math.round(value).toLocaleString();
      },
    });

    return () => controls.stop();
  }, [value, duration, inView]);

  return <span ref={nodeRef}>0</span>;
}

// Hero Section
function HeroSection() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);
  
  const slides = [
    {
      image: "https://images.unsplash.com/photo-1540962351504-03099e0a754b?q=80&w=2574&auto=format&fit=crop",
      title: "WORLD-CLASS",
      subtitle: "FBO SERVICES",
    },
    {
      image: "https://images.unsplash.com/photo-1474302770737-173ee21bab63?q=80&w=2574&auto=format&fit=crop",
      title: "LUXURY",
      subtitle: "AWAITS",
    },
    {
      image: "https://images.unsplash.com/photo-1583416750470-965b2707b355?q=80&w=2574&auto=format&fit=crop",
      title: "SEYCHELLES",
      subtitle: "PARADISE",
    },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section id="home" className="relative h-screen overflow-hidden">
      {/* Background Slides */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.5 }}
          className="absolute inset-0"
        >
          <img
            src={slides[currentSlide].image}
            alt={`${slides[currentSlide].title} ${slides[currentSlide].subtitle}`}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/40 to-black/90" />
        </motion.div>
      </AnimatePresence>

      {/* Content */}
      <motion.div 
        style={{ y }}
        className="relative z-10 h-full flex flex-col justify-center items-center text-center px-6"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mb-6 text-gold/80 tracking-[0.4em] uppercase text-sm font-medium border border-gold/30 px-4 py-2 rounded-full backdrop-blur-sm bg-black/30"
        >
          Euro Aviation Limited • Seychelles
        </motion.div>
        
        <div className="overflow-hidden">
          <motion.h1
            key={currentSlide + "title"}
            initial={{ y: 100 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.8, ease: "circOut" }}
            className="text-5xl sm:text-7xl lg:text-9xl font-black text-white tracking-tighter leading-none mb-2"
          >
            {slides[currentSlide].title}
          </motion.h1>
        </div>
        
        <div className="overflow-hidden">
          <motion.h2
            key={currentSlide + "subtitle"}
            initial={{ y: 100 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: "circOut" }}
            className="text-4xl sm:text-6xl lg:text-8xl font-black text-transparent bg-clip-text bg-gradient-to-r from-gold via-yellow-200 to-gold tracking-tighter leading-none pb-2"
          >
            {slides[currentSlide].subtitle}
          </motion.h2>
        </div>
        
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 1 }}
          className="mt-8 max-w-2xl text-white/70 text-lg sm:text-xl font-light leading-relaxed"
        >
          Premier Fixed Base Operator services at Seychelles International Airport.
          Where luxury meets efficiency, since 2016.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <motion.a
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            href="#services"
            className="px-8 py-4 bg-gold text-black font-bold tracking-wider uppercase hover:bg-gold-light transition-all flex items-center gap-2 justify-center group shadow-lg shadow-gold/20"
          >
            Explore Services
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </motion.a>
          <motion.a
            whileHover={{ scale: 1.05, backgroundColor: "rgba(255,255,255,0.1)" }}
            whileTap={{ scale: 0.95 }}
            href="#contact"
            className="px-8 py-4 border-2 border-white/30 text-white font-bold tracking-wider uppercase hover:border-gold hover:text-gold transition-all"
          >
            Contact Us
          </motion.a>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.a
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          href="#services"
          className="absolute bottom-12 text-white/30 hover:text-gold transition-colors"
          aria-label="Scroll down"
        >
          <ChevronDown size={32} />
        </motion.a>
      </motion.div>
    </section>
  );
}

// Stats Section
function StatsSection() {
  const stats = [
    { number: 2016, label: "Established", suffix: "" },
    { number: 24, label: "Operations (hrs)", suffix: "/7" },
    { number: 1000, label: "Flights Handled", suffix: "+" },
    { number: 4, label: "VIP Lounges", suffix: "" },
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-black to-zinc-950/50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              viewport={{ once: true }}
              className="text-center p-8 border border-white/5 bg-white/5 hover:bg-white/10 hover:border-gold/30 transition-all rounded-sm backdrop-blur-sm group"
            >
              <div className="text-4xl sm:text-5xl font-black text-gold group-hover:scale-110 transition-transform duration-300">
                <Counter value={stat.number} duration={2.5} />
                {stat.suffix}
              </div>
              <div className="mt-2 text-white/50 uppercase tracking-widest text-xs font-semibold">
                {stat.label}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Services Section
function ServicesSection() {
  const services = [
    {
      icon: Plane,
      title: "Aircraft Handling",
      description:
        "Professional ground handling services with our highly trained team ensuring seamless operations.",
      features: ["Heavy Aircraft Capable", "Pushback Services", "GPU & ASU Available", "Quick Turnaround"],
    },
    {
      icon: Users,
      title: "VIP Meet & Greet",
      description:
        "Personalized arrival and departure services for VIP clients with dedicated staff.",
      features: ["Tarmac Reception", "Customs Fast-Track", "Luggage Porter", "Personal Escort"],
    },
    {
      icon: Building,
      title: "VIP Lounges",
      description:
        "Four exclusive lounges offering serene, sophisticated environments for pre-flight relaxation.",
      features: ["Private Suites", "Shower Facilities", "Business Center", "Gourmet Catering"],
    },
    {
      icon: Fuel,
      title: "Fuel Services",
      description:
        "Comprehensive aviation fuel services with competitive pricing and efficient delivery.",
      features: ["JET A-1 Available", "Competitive Rates", "High Flow Trucks", "24/7 Availability"],
    },
    {
      icon: Shield,
      title: "Security & Privacy",
      description:
        "Discrete and secure services ensuring complete confidentiality for high-profile clients.",
      features: ["Private Access", "Secure Parking", "Confidential Handling", "Airside Transfers"],
    },
    {
      icon: Clock,
      title: "Concierge Services",
      description:
        "Full-service concierge handling all travel arrangements from hotel bookings to transport.",
      features: ["Hotel Reservations", "Limousine Service", "In-flight Catering", "Tour Packages"],
    },
  ];

  return (
    <section id="services" className="py-32 relative">
      {/* Background Noise/Gradient */}
      <div className="absolute inset-0 bg-transparent opacity-20 pointer-events-none" 
           style={{ backgroundImage: "url('data:image/svg+xml,...')" }} />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <span className="text-gold tracking-[0.3em] uppercase text-sm font-medium px-3 py-1 border border-gold/20 rounded-full bg-gold/5">
            What We Offer
          </span>
          <h2 className="mt-6 text-5xl sm:text-7xl font-black text-white tracking-tight">
            OUR <span className="text-gold">SERVICES</span>
          </h2>
          <p className="mt-6 max-w-2xl mx-auto text-white/60 text-lg">
            Comprehensive FBO services tailored to exceed the expectations of discerning travelers
            and aviation professionals.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <motion.article
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -10, transition: { duration: 0.2 } }}
              className="group p-8 bg-zinc-900/40 backdrop-blur-md border border-white/10 hover:border-gold/50 transition-all rounded-sm hover:shadow-[0_10px_40px_-10px_rgba(212,175,55,0.1)]"
            >
              <div className="w-14 h-14 bg-gold/10 rounded-full flex items-center justify-center mb-6 group-hover:bg-gold transition-colors duration-500">
                <service.icon className="w-7 h-7 text-gold group-hover:text-black transition-colors duration-500" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-gold transition-colors">{service.title}</h3>
              <p className="text-white/60 mb-8 leading-relaxed text-sm h-12">{service.description}</p>
              
              <div className="space-y-3 pt-6 border-t border-white/5">
                {service.features.map((feature, i) => (
                  <div key={i} className="text-white/50 text-xs uppercase tracking-wide flex items-center gap-3">
                    <CheckCircle2 className="w-4 h-4 text-gold/60" />
                    {feature}
                  </div>
                ))}
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}

// About Section
function AboutSection() {
  return (
    <section id="about" className="py-32 bg-black relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_var(--tw-gradient-stops))] from-gold to-transparent opacity-20" />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-gold tracking-[0.3em] uppercase text-sm font-medium">Since 2016</span>
            <h2 className="mt-4 text-5xl sm:text-7xl font-black text-white tracking-tight leading-none">
              COMMITMENT TO <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold to-yellow-200">EXCELLENCE</span>
            </h2>
            <div className="mt-8 space-y-6 text-white/70 text-lg leading-relaxed font-light">
              <p>
                Euro Aviation Limited is strategically situated at Seychelles International Airport,
                on Mahé Island, in close proximity to Victoria, the capital city.
              </p>
              <p>
                Since 2016, our highly specialized team has been committed to delivering exclusive
                services tailored for VVIP passengers and aircraft crew. We prioritize discretion
                and security with airside transfers available between aircraft, VIP facilities,
                and both domestic and international terminals.
              </p>
              <p>
                What distinguishes Euro Aviation from other providers is not just a commitment but
                a dedication to redefining the aviation experience.
              </p>
            </div>

            <div className="mt-12 grid grid-cols-2 gap-8 border-t border-white/10 pt-8">
              <div>
                <h4 className="text-white font-bold text-lg mb-2">For Operators</h4>
                <ul className="space-y-2 text-white/50 text-sm">
                  <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-gold rounded-full"/> Quick Turnarounds</li>
                  <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-gold rounded-full"/> Flight Planning Support</li>
                  <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-gold rounded-full"/> Weather Reports</li>
                </ul>
              </div>
              <div>
                <h4 className="text-white font-bold text-lg mb-2">For Passengers</h4>
                <ul className="space-y-2 text-white/50 text-sm">
                  <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-gold rounded-full"/> Seamless Transfers</li>
                  <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-gold rounded-full"/> Luxury Transportation</li>
                  <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-gold rounded-full"/> Private Waiting Areas</li>
                </ul>
              </div>
            </div>
          </motion.div>

          <div className="relative">
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="aspect-[4/5] overflow-hidden rounded-sm relative z-10"
            >
              <div className="absolute inset-0 bg-gold/20 mix-blend-overlay z-10 pointer-events-none" />
              <img
                src="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?q=80&w=2574&auto=format&fit=crop"
                alt="Private jet on runway at sunset - Euro Aviation Seychelles FBO"
                className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
              />
            </motion.div>
            
            {/* Floating Element */}
            <motion.div 
              style={{ y: useTransform(useScroll().scrollY, [0, 1000], [0, -50]) }}
              className="absolute -bottom-10 -left-10 z-20 bg-zinc-900 border border-white/10 p-8 w-64 shadow-2xl shadow-black/50"
            >
              <div className="flex text-gold mb-4">
                <Star className="fill-gold" />
                <Star className="fill-gold" />
                <Star className="fill-gold" />
                <Star className="fill-gold" />
                <Star className="fill-gold" />
              </div>
              <blockquote className="text-white/80 font-medium italic mb-4 leading-relaxed">
                "Where luxury meets efficiency in the heart of paradise."
              </blockquote>
              <div className="text-white/40 text-xs uppercase tracking-widest">
                Euro Aviation
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Gallery Section
function GallerySection() {
  const images = [
    {
      url: "https://images.unsplash.com/photo-1559268950-2d7ceb2efa3a?q=80&w=1964&auto=format&fit=crop",
      title: "VIP Lounge",
      span: "col-span-2 row-span-2",
    },
    {
      url: "https://images.unsplash.com/photo-1540962351504-03099e0a754b?q=80&w=2574&auto=format&fit=crop",
      title: "Aircraft Handling",
      span: "col-span-1 row-span-1",
    },
    {
      url: "https://images.unsplash.com/photo-1569629743817-70d8db6c323b?q=80&w=2598&auto=format&fit=crop",
      title: "Seychelles Beach",
      span: "col-span-1 row-span-1",
    },
    {
      url: "https://images.unsplash.com/photo-1583416750470-965b2707b355?q=80&w=2574&auto=format&fit=crop",
      title: "Paradise Island",
      span: "col-span-1 row-span-2",
    },
    {
      url: "https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?q=80&w=2669&auto=format&fit=crop",
      title: "Ground Services",
      span: "col-span-1 row-span-1",
    },
    {
      url: "https://images.unsplash.com/photo-1474302770737-173ee21bab63?q=80&w=2574&auto=format&fit=crop",
      title: "Private Aviation",
      span: "col-span-1 row-span-1",
    },
  ];

  return (
    <section id="gallery" className="py-32 bg-zinc-950">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-gold tracking-[0.3em] uppercase text-sm font-medium">Visual Journey</span>
          <h2 className="mt-4 text-5xl sm:text-7xl font-black text-white tracking-tight">
            OUR <span className="text-gold">GALLERY</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 auto-rows-[200px]">
          {images.map((image, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: index * 0.1 }}
              viewport={{ once: true }}
              className={`relative overflow-hidden group ${image.span}`}
            >
              <img
                src={image.url}
                alt={`${image.title} - Euro Aviation Seychelles FBO services`}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 filter saturate-50 group-hover:saturate-100"
              />
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                <div>
                  <div className="text-gold font-bold text-lg uppercase tracking-wider translate-y-4 group-hover:translate-y-0 transition-transform duration-500">{image.title}</div>
                  <div className="text-white/60 text-xs uppercase tracking-widest mt-1 translate-y-4 group-hover:translate-y-0 transition-transform duration-500 delay-75">View</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Contact Section
function ContactSection() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    aircraft: "",
    message: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    window.location.href = `mailto:fbo.fsia@euroaviation.sc?subject=Handling Request from ${formData.name}&body=${formData.message}`;
  };

  return (
    <section id="contact" className="py-32 bg-black relative">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-gold tracking-[0.3em] uppercase text-sm font-medium">Get In Touch</span>
            <h2 className="mt-4 text-5xl sm:text-7xl font-black text-white tracking-tight">
              REQUEST <span className="text-gold">HANDLING</span>
            </h2>
            <p className="mt-6 text-white/60 text-lg leading-relaxed">
              Ready to experience world-class FBO services? Contact our team for handling requests,
              quotes, or any inquiries about our services.
            </p>

            <div className="mt-12 space-y-8">
              <a href="https://maps.google.com" target="_blank" rel="noreferrer" className="flex items-start gap-4 group cursor-pointer hover:bg-white/5 p-4 -mx-4 rounded-xl transition-colors">
                <div className="p-3 bg-white/5 rounded-full text-gold group-hover:bg-gold group-hover:text-black transition-colors">
                  <MapPin className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-white font-bold text-lg">Location</div>
                  <div className="text-white/60 group-hover:text-white/80 transition-colors">
                    Seychelles International Airport<br />
                    Mahé Island, Seychelles
                  </div>
                </div>
              </a>
              
              <div className="flex items-start gap-4 group hover:bg-white/5 p-4 -mx-4 rounded-xl transition-colors">
                <div className="p-3 bg-white/5 rounded-full text-gold group-hover:bg-gold group-hover:text-black transition-colors">
                  <Phone className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-white font-bold text-lg">Phone</div>
                  <div className="text-white/60">
                    <a href="tel:+2482558847" className="hover:text-gold transition-colors block">+248 255 8847</a>
                    <a href="tel:+2482592849" className="hover:text-gold transition-colors block">+248 259 2849</a>
                  </div>
                </div>
              </div>
              
              <a href="mailto:fbo.fsia@euroaviation.sc" className="flex items-start gap-4 group hover:bg-white/5 p-4 -mx-4 rounded-xl transition-colors">
                <div className="p-3 bg-white/5 rounded-full text-gold group-hover:bg-gold group-hover:text-black transition-colors">
                  <Mail className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-white font-bold text-lg">Email</div>
                  <div className="text-white/60 group-hover:text-gold transition-colors">
                    fbo.fsia@euroaviation.sc
                  </div>
                </div>
              </a>
            </div>

            <div className="mt-12">
              <div className="text-white/40 uppercase tracking-wider text-sm mb-4">Follow Us</div>
              <div className="flex gap-4">
                {[
                  { icon: Instagram, href: "https://www.instagram.com/euro_aviation_ltd_seychelles_/" },
                  { icon: Linkedin, href: "https://www.linkedin.com/company/euro-aviation-ltd-seychelles/" },
                  { icon: Facebook, href: "https://www.facebook.com/EuroAviation.L.S" },
                  { icon: Twitter, href: "https://twitter.com/EuroaviationLtd" }
                ].map((social, idx) => (
                  <a
                    key={idx}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 border border-white/20 flex items-center justify-center hover:bg-gold hover:border-gold hover:text-black text-white/60 transition-all rounded-full"
                  >
                    <social.icon size={20} />
                  </a>
                ))}
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <form onSubmit={handleSubmit} className="space-y-6 bg-zinc-950 p-8 border border-white/10 relative overflow-hidden group">
              <div className="absolute top-0 left-0 w-1 h-full bg-gold transform origin-top scale-y-0 group-hover:scale-y-100 transition-transform duration-500" />
              
              <div className="relative z-10">
                <h3 className="text-2xl font-bold text-white mb-6">Send Handling Request</h3>
                
                <div className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-white/60 text-xs uppercase tracking-wider mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full bg-black/50 border border-white/20 px-4 py-3 text-white focus:border-gold focus:outline-none transition-colors"
                      placeholder="Your name"
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="email" className="block text-white/60 text-xs uppercase tracking-wider mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full bg-black/50 border border-white/20 px-4 py-3 text-white focus:border-gold focus:outline-none transition-colors"
                        placeholder="your@email.com"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-white/60 text-xs uppercase tracking-wider mb-2">
                        Phone
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full bg-black/50 border border-white/20 px-4 py-3 text-white focus:border-gold focus:outline-none transition-colors"
                        placeholder="+1 234 567 890"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="aircraft" className="block text-white/60 text-xs uppercase tracking-wider mb-2">
                      Aircraft Type
                    </label>
                    <input
                      type="text"
                      id="aircraft"
                      value={formData.aircraft}
                      onChange={(e) => setFormData({ ...formData, aircraft: e.target.value })}
                      className="w-full bg-black/50 border border-white/20 px-4 py-3 text-white focus:border-gold focus:outline-none transition-colors"
                      placeholder="e.g. Gulfstream G650"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-white/60 text-xs uppercase tracking-wider mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full bg-black/50 border border-white/20 px-4 py-3 text-white focus:border-gold focus:outline-none transition-colors resize-none"
                      placeholder="Tell us about your requirements..."
                    />
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    className="w-full py-4 bg-gold text-black font-bold uppercase tracking-wider hover:bg-gold-light transition-colors flex items-center justify-center gap-2"
                  >
                    Send Request
                    <ArrowRight className="w-4 h-4" />
                  </motion.button>
                </div>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// Footer Component (Unchanged mostly but wrapped)
function Footer() {
  return (
    <footer className="py-12 bg-zinc-950 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full border-2 border-gold flex items-center justify-center">
              <span className="text-gold font-bold text-xs">EA</span>
            </div>
            <div>
              <span className="text-gold font-semibold tracking-wider">EURO AVIATION</span>
              <span className="text-white/40 text-sm block">Seychelles</span>
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-8 text-white/40 text-sm">
            <a href="#home" className="hover:text-gold transition-colors">Home</a>
            <a href="#services" className="hover:text-gold transition-colors">Services</a>
            <a href="#about" className="hover:text-gold transition-colors">About</a>
            <a href="#gallery" className="hover:text-gold transition-colors">Gallery</a>
            <a href="#contact" className="hover:text-gold transition-colors">Contact</a>
          </div>

          <div className="text-white/30 text-sm text-center md:text-right">
            © {new Date().getFullYear()} Euro Aviation Limited.<br />
            All rights reserved.
          </div>
        </div>
      </div>
    </footer>
  );
}

// Main App Component
export function App() {
  return (
    <div className="min-h-screen bg-black text-white font-sans antialiased selection:bg-gold selection:text-black">
      <Navigation />
      <main>
        <HeroSection />
        <StatsSection />
        <ServicesSection />
        <AboutSection />
        <GallerySection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
